import { useCallback, useMemo, useState } from "react";
import { VStack } from "@design-system/VStack/VStack";
import { HStack } from "@design-system/HStack/HStack";
import { useLineItemsBulkCommandActions } from "@components/lineItems/useLineItemsBulkCommandActions";
import { EagerLoaded } from "@common/types/base/orm";
import { FinancialAccount } from "@common/types/financialAccount";
import { isInvoiceProviderAccount } from "@helpers/invoice";
import {
    Invoice,
    InvoiceSource,
    InvoiceViewOptionGroup,
} from "@common/types/invoice";
import { CheckIcon } from "@icons";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { InvoiceLineItemsViewContainer } from "@components/invoices/views/invoiceLineItemsView/InvoiceLineItemsViewContainer";
import { SplitButton } from "@design-system/Button/SplitButton";
import { Menu } from "@design-system/Menu/Menu.lib";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import { noop } from "@common/helpers/function";
import { CreateStripeConnection } from "@components/stripe/CreateStripeConnection";
import { CreateMercuryConnection } from "@components/mercury/CreateMercuryConnection";
import { CreateBillcomConnection } from "@components/billcom/CreateBillcomConnection";
import { ForwardViaEmailModal } from "@components/accrualDocumentImporter/ForwardViaEmailModal";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsRecognitionScheduleColumnEnabled } from "@hooks/useIsRecognitionScheduleColumnEnabled";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { DetailsAside } from "@design-system/DetailsAside/DetailsAside";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import { INVOICE_PAGE_FILTER_TABS } from "./invoicePageFilterTabs";
import { AccountSelectModal } from "./AccountSelectModal";
import { InvoiceDetails } from "./InvoiceDetails";
import { InvoiceStats } from "./InvoiceStats";
import { PaginatedInvoicesTableContextProvider } from "./PaginatedInvoicesTableContextProvider";
import styles from "./InvoicePageContent.module.scss";
import { InvoicesByCustomerViewContainer } from "./views/invoicesByCustomerView/InvoicesByCustomerViewContainer";
import { InvoicesByCounterpartyTableContextProvider } from "./InvoicesByCounterpartyTableContextProvider";
import { InvoicesViewContainer } from "./views/invoicesView/InvoicesViewContainer";
import { createInvoicesViewConfig } from "./views/invoicesView/invoicesViewConfig";
import { invoicesByCustomerViewConfig } from "./views/invoicesByCustomerView/invoicesByCustomerViewConfig";
import { ManualInvoiceEditDrawer } from "./ManualInvoiceEditDrawer/ManualInvoiceEditDrawer";
import { InvoiceAutoSelector } from "./InvoiceAutoSelector";
import { useSearchParams } from "react-router-dom";
import { useStripeExpressReturn } from "@hooks/useStripeExpressReturn";
import {
    injectScheduleColumn,
    SCHEDULE_TEMPLATE_SEARCH_PARAM,
} from "./views/scheduleColumnHandoff";
import { useInvoicePage } from "./useInvoicePage";
import { useInvoiceDetailsScheduleCallbacks } from "./useInvoiceDetailsScheduleCallbacks";
import { InvoicesExport } from "./InvoicesExport";

const GROUP_BY_LABELS: Record<InvoiceViewOptionGroup, string> = {
    [InvoiceViewOptionGroup.INVOICE]: "By invoice",
    [InvoiceViewOptionGroup.COUNTERPARTY]: "By customer",
    [InvoiceViewOptionGroup.LINE_ITEMS]: "By line item",
};

interface Props {
    invoicingAccounts: FinancialAccount[];
}

export const InvoicePageContent: React.FC<Props> = ({ invoicingAccounts }) => {
    useStripeExpressReturn();
    const {
        selectedInvoice,
        setSelectedInvoice,
        showAccountSelectModal,
        setShowAccountSelectModal,
        accountSelectAccounts,
        invoiceTableView,
        invoiceLineItemsTableView,
        invoicesByCustomerTableView,
        isManualAccrualCreationEnabled,
        openOverlay,
        hasVisibleAccrualLedgerColumn,
        invoiceLineItemsViewConfig,
        bulkUpdateInvoiceLineItems,
        handleUpdateInvoiceMemo,
        handleUpdateInvoiceStatus,
        handleUpdateInvoiceDueDate,
        viewOptions,
        builtInInvoiceViews,
        builtInInvoiceLineItemViews,
        builtInInvoicesByCustomerViews,
        filters,
        setFilters,
        viewOption,
        invoiceStats,
        initialIds,
        selectedInvoiceLineItems,
        hasSelectedByCurrentFilters,
        selectedInvoiceLineItemsCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsReceivableSelected,
        bulkAccrualAccountEntityId,
        invoiceRowSelection,
        setInvoiceRowSelection,
        editInvoices,
        setEditInvoices,
        clearInvoiceSelection,
        hasStripeAccounts,
        stripeConnectionRef,
        mercuryConnectionRef,
        billcomConnectionRef,
        hasMercuryAccounts,
        hasBillcomAccounts,
        onCreateStripeInvoice,
        onCreateBillcomInvoice,
        onAccountSelect,
        onInvoiceClick,
        handleFiltersChange,
        handleDateRangeChange,
        defaultFilters,
        dateRange,
        handleViewOptionChange,
        handleLineItemsEditClick,
        isLineItemsEditAvailable,
        handleInvoiceViewChange,
        handleInvoiceViewSelect,
        handleInvoiceLineItemsViewChange,
        handleInvoiceLineItemsViewSelect,
        handleInvoicesByCustomerViewChange,
        handleInvoicesByCustomerViewSelect,
        handleClearFilters,
    } = useInvoicePage(invoicingAccounts);
    const { billInvoiceImporter: isDocumentImporterEnabled } = useTypedFlags();
    const isSchedulesEnabled = useIsRecognitionScheduleColumnEnabled();
    // ACCRUAL_LEDGER also gates the accrual-basis bulk line-item actions
    // below ("Accrual ledger", "Receivables account") — same plan feature
    // as the importer entry points, so we share the single hook call.
    const { hasAccess: hasAccrualLedgerAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const hasImporterAccess = hasAccrualLedgerAccess;
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );

    const viewOptionItems = useMemo<
        Array<PickerItemData<InvoiceViewOptionGroup>>
    >(
        () =>
            viewOptions.map((option) => ({
                value: option,
                label: GROUP_BY_LABELS[option],
            })),
        [viewOptions],
    );

    const hasConnectedInvoiceAccounts = useMemo(
        () => invoicingAccounts.some(isInvoiceProviderAccount),
        [invoicingAccounts],
    );

    // The primary "Create invoice" action creates a Kick native (manual)
    // invoice. The checkmark marks that source.
    const activeInvoiceSource = InvoiceSource.MANUAL;

    const renderCreateInvoiceSourceLabel = (
        label: string,
        source: InvoiceSource,
    ) => (
        <HStack
            as="span"
            gap={8}
            align="center"
            justify="between"
            className={styles.createInvoiceSourceLabel}
        >
            {label}
            {activeInvoiceSource === source && (
                <CheckIcon className="icon-color-gray-700 icon-size-text" />
            )}
        </HStack>
    );

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const onCreateNativeInvoice = useCallback(() => {
        // Opens a create form the server can only refuse on save, after the
        // user has filled it in.
        if (refuseIfViewOnly()) {
            return;
        }

        openOverlay("nativeInvoiceDrawer", {});
    }, [openOverlay, refuseIfViewOnly]);

    const onCreateMercuryInvoice = useCallback(() => {
        // The next step leaves the app for the provider, so the server never
        // gets to refuse it.
        if (refuseIfViewOnly()) {
            return;
        }

        if (!hasMercuryAccounts) {
            mercuryConnectionRef.current?.initializeConnection();
            return;
        }
        window.open(
            "https://app.mercury.com/invoicing/create-invoice",
            "_blank",
            "noopener,noreferrer",
        );
    }, [hasMercuryAccounts, mercuryConnectionRef, refuseIfViewOnly]);

    const onCreateImporterInvoice = useCallback(() => {
        // Opens a create form the server can only refuse on save, after the
        // user has filled it in.
        if (refuseIfViewOnly()) {
            return;
        }

        openOverlay("manualInvoiceCreation", {});
    }, [openOverlay, refuseIfViewOnly]);

    const [isForwardOpen, setIsForwardOpen] = useState(false);
    const onForwardViaEmail = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }

        setIsForwardOpen(true);
    }, [refuseIfViewOnly]);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_INVOICING_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.invoicing,
    });

    const lineItemsBulkActions = useLineItemsBulkCommandActions({
        selectedCount: selectedInvoiceLineItemsCount,
        hasSelectedByCurrentFilters,
        isUpdating: bulkUpdateInvoiceLineItems.isPending,
        hasReceivablesAccount: !!bulkAccrualAccountEntityId,
        hasPayablesAccount: false,
        hasClassesAccess,
        hasAccrualLedgerAccess,
        accrualAccountEntityId: bulkAccrualAccountEntityId,
        onClassesSelected: handleBulkClassesSelected,
        onAccrualAccountSelected: handleBulkAccrualAccountSelected,
        onAccountsReceivableSelected: handleBulkAccountsReceivableSelected,
        onEditClick: isLineItemsEditAvailable
            ? handleLineItemsEditClick
            : undefined,
    });

    const [scheduleHandoffParams] = useSearchParams();
    // Arriving from the Apply drawer's "Add invoices" card: force the Schedule
    // column on so the user lands somewhere they can actually apply.
    const deepLinkScheduleTemplateId =
        scheduleHandoffParams.get(SCHEDULE_TEMPLATE_SEARCH_PARAM) ?? undefined;
    const isScheduleColumnEnsured = deepLinkScheduleTemplateId != null;
    const visibleInvoiceTableView = useMemo(
        () =>
            isScheduleColumnEnsured && isSchedulesEnabled
                ? injectScheduleColumn(invoiceTableView)
                : invoiceTableView,
        [invoiceTableView, isScheduleColumnEnsured, isSchedulesEnabled],
    );

    const exportColumns = useMemo(() => {
        if (viewOption.groupBy === InvoiceViewOptionGroup.LINE_ITEMS) {
            return invoiceLineItemsTableView.columns.map((column) => column.id);
        }
        if (viewOption.groupBy === InvoiceViewOptionGroup.COUNTERPARTY) {
            return invoicesByCustomerTableView.columns.map(
                (column) => column.id,
            );
        }
        return visibleInvoiceTableView.columns.map((column) => column.id);
    }, [
        viewOption.groupBy,
        invoiceLineItemsTableView,
        invoicesByCustomerTableView,
        visibleInvoiceTableView,
    ]);

    const {
        scheduleCallbacks: invoiceDetailsScheduleCallbacks,
        scheduleOverlays,
    } = useInvoiceDetailsScheduleCallbacks(setSelectedInvoice);
    const {
        onRequestScheduleConfirm,
        onScheduleApplied,
        onCreateSchedule,
        onRequestLifecycleAction,
    } = invoiceDetailsScheduleCallbacks;

    const invoiceViewConfig = useMemo(
        () =>
            createInvoicesViewConfig({
                isSchedulesEnabled,
            }),
        [isSchedulesEnabled],
    );

    const viewToolbarContent = useMemo(() => {
        if (viewOption.groupBy === InvoiceViewOptionGroup.INVOICE) {
            return (
                <ViewToolbar
                    // The toolbar persists this view verbatim on save, so it
                    // gets the user's real one — never the deep-link column,
                    // which would otherwise bake into their saved layout.
                    view={invoiceTableView}
                    onViewChange={handleInvoiceViewChange}
                    onViewSelect={handleInvoiceViewSelect}
                    viewConfig={invoiceViewConfig}
                    builtInViews={builtInInvoiceViews}
                    defaultFilters={defaultFilters}
                    baseViewCacheKey={
                        WORKSPACE_USER_CACHE_KEYS.INVOICES_PAGE_INVOICE_BASE_VIEW_ID_CACHE_KEY
                    }
                />
            );
        }
        if (viewOption.groupBy === InvoiceViewOptionGroup.LINE_ITEMS) {
            return (
                <ViewToolbar
                    view={invoiceLineItemsTableView}
                    onViewChange={handleInvoiceLineItemsViewChange}
                    onViewSelect={handleInvoiceLineItemsViewSelect}
                    viewConfig={invoiceLineItemsViewConfig}
                    builtInViews={builtInInvoiceLineItemViews}
                    defaultFilters={defaultFilters}
                    baseViewCacheKey={
                        WORKSPACE_USER_CACHE_KEYS.INVOICES_PAGE_LINE_ITEMS_BASE_VIEW_ID_CACHE_KEY
                    }
                />
            );
        }
        if (viewOption.groupBy === InvoiceViewOptionGroup.COUNTERPARTY) {
            return (
                <ViewToolbar
                    view={invoicesByCustomerTableView}
                    onViewChange={handleInvoicesByCustomerViewChange}
                    onViewSelect={handleInvoicesByCustomerViewSelect}
                    viewConfig={invoicesByCustomerViewConfig}
                    builtInViews={builtInInvoicesByCustomerViews}
                    defaultFilters={defaultFilters}
                    baseViewCacheKey={
                        WORKSPACE_USER_CACHE_KEYS.INVOICES_PAGE_BY_CUSTOMER_BASE_VIEW_ID_CACHE_KEY
                    }
                />
            );
        }
        return null;
    }, [
        viewOption.groupBy,
        invoiceTableView,
        handleInvoiceViewChange,
        handleInvoiceViewSelect,
        invoiceViewConfig,
        builtInInvoiceViews,
        defaultFilters,
        invoiceLineItemsTableView,
        handleInvoiceLineItemsViewChange,
        handleInvoiceLineItemsViewSelect,
        invoiceLineItemsViewConfig,
        builtInInvoiceLineItemViews,
        invoicesByCustomerTableView,
        handleInvoicesByCustomerViewChange,
        handleInvoicesByCustomerViewSelect,
        builtInInvoicesByCustomerViews,
    ]);

    useSubNavigationPortalContent("invoices-view-toolbar", viewToolbarContent);

    return (
        <>
            <TablePageHeader
                search={filters?.search}
                onSearchChange={(value) => {
                    handleFiltersChange({ search: value ?? "" });
                }}
                filters={filters ?? {}}
                onFiltersChange={handleFiltersChange}
                defaultFilters={defaultFilters}
                hideClearButton
                availableFilters={INVOICE_PAGE_FILTER_TABS}
                accountFilterPredicate={isInvoiceProviderAccount}
                shouldShowKickAccountOption
                counterpartyEntityLabel="Customer"
                counterpartyInvoicesOnly
                isFullPage
                borderBottom
            >
                <div className={styles.buttonGroup}>
                    <DateRangeFilterNext
                        buttonClass={styles.dateRangeFilter}
                        dateRange={dateRange}
                        onChange={handleDateRangeChange}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                    />
                    <Picker.Select
                        items={viewOptionItems}
                        value={viewOption.groupBy}
                        onValueChange={(value) =>
                            handleViewOptionChange(
                                value ?? InvoiceViewOptionGroup.INVOICE,
                            )
                        }
                        trigger={
                            <Picker.Button
                                variant="primary"
                                size="md"
                                className={styles.viewOptionsPopover}
                            />
                        }
                    />
                </div>
                <EntityFilterCombobox
                    filters={filters ?? {}}
                    onChange={handleFiltersChange}
                    onlyBusiness
                    excludeAllMockEntities
                    showLabel={false}
                />
                <InvoicesExport
                    filters={filters}
                    groupBy={viewOption.groupBy}
                    columns={exportColumns}
                />
                <SplitButton
                    onPrimaryClick={onCreateNativeInvoice}
                    menuAriaLabel="More invoice options"
                    menu={
                        <>
                            <Menu.Item onClick={onCreateNativeInvoice}>
                                {renderCreateInvoiceSourceLabel(
                                    "Kick",
                                    InvoiceSource.MANUAL,
                                )}
                            </Menu.Item>
                            <Menu.Item onClick={onCreateStripeInvoice}>
                                {renderCreateInvoiceSourceLabel(
                                    "Stripe",
                                    InvoiceSource.STRIPE,
                                )}
                            </Menu.Item>
                            <Menu.Item onClick={onCreateMercuryInvoice}>
                                {renderCreateInvoiceSourceLabel(
                                    "Mercury",
                                    InvoiceSource.MERCURY,
                                )}
                            </Menu.Item>
                            <Menu.Item onClick={onCreateBillcomInvoice}>
                                {renderCreateInvoiceSourceLabel(
                                    "BILL",
                                    InvoiceSource.BILLCOM,
                                )}
                            </Menu.Item>
                            {isManualAccrualCreationEnabled &&
                                hasImporterAccess && (
                                    <Menu.Item
                                        onClick={onCreateImporterInvoice}
                                    >
                                        {renderCreateInvoiceSourceLabel(
                                            "Import",
                                            InvoiceSource.IMPORTER,
                                        )}
                                    </Menu.Item>
                                )}
                            {isManualAccrualCreationEnabled &&
                                hasImporterAccess &&
                                isDocumentImporterEnabled && (
                                    <Menu.Item onClick={onForwardViaEmail}>
                                        Forward via email
                                    </Menu.Item>
                                )}
                        </>
                    }
                >
                    Create invoice
                </SplitButton>
            </TablePageHeader>

            <VStack gap={16} className="p-0">
                <InvoiceStats
                    invoiceStats={invoiceStats}
                    filters={filters}
                    setFilters={setFilters}
                />
                <div className={styles.viewContainer}>
                    {viewOption.groupBy === InvoiceViewOptionGroup.INVOICE && (
                        <PaginatedInvoicesTableContextProvider>
                            {initialIds?.length && (
                                <InvoiceAutoSelector
                                    initialIds={initialIds}
                                    onSelect={setSelectedInvoice}
                                />
                            )}
                            <InvoicesViewContainer
                                view={visibleInvoiceTableView}
                                hasConnectedInvoiceAccounts={
                                    hasConnectedInvoiceAccounts
                                }
                                onClearFilters={handleClearFilters}
                                isSchedulesEnabled={isSchedulesEnabled}
                                prioritizedScheduleTemplateId={
                                    deepLinkScheduleTemplateId
                                }
                                onRequestScheduleConfirm={
                                    onRequestScheduleConfirm
                                }
                                onRequestLifecycleAction={
                                    onRequestLifecycleAction
                                }
                                onScheduleApplied={onScheduleApplied}
                                onCreateSchedule={onCreateSchedule}
                                onInvoiceClick={onInvoiceClick}
                                selectedInvoiceId={selectedInvoice?.id}
                                rowSelection={invoiceRowSelection}
                                onRowSelectionChange={setInvoiceRowSelection}
                                onClearSelection={clearInvoiceSelection}
                                onEditInvoices={
                                    isManualAccrualCreationEnabled
                                        ? setEditInvoices
                                        : undefined
                                }
                            />
                        </PaginatedInvoicesTableContextProvider>
                    )}
                    {viewOption.groupBy ===
                        InvoiceViewOptionGroup.LINE_ITEMS && (
                        <PaginatedInvoicesTableContextProvider>
                            {initialIds?.length && (
                                <InvoiceAutoSelector
                                    initialIds={initialIds}
                                    onSelect={setSelectedInvoice}
                                />
                            )}
                            <InvoiceLineItemsViewContainer
                                isSchedulesEnabled={isSchedulesEnabled}
                                prioritizedScheduleTemplateId={
                                    deepLinkScheduleTemplateId
                                }
                                onRequestScheduleConfirm={
                                    onRequestScheduleConfirm
                                }
                                onRequestLifecycleAction={
                                    onRequestLifecycleAction
                                }
                                onScheduleApplied={onScheduleApplied}
                                onCreateSchedule={onCreateSchedule}
                                view={invoiceLineItemsTableView}
                                hasConnectedInvoiceAccounts={
                                    hasConnectedInvoiceAccounts
                                }
                                onClearFilters={handleClearFilters}
                                hasAnyAccrualLedgers={
                                    hasVisibleAccrualLedgerColumn
                                }
                                onInvoiceClick={onInvoiceClick}
                                selectedInvoiceId={selectedInvoice?.id}
                                selectedLineItems={selectedInvoiceLineItems}
                                hasSelectedByCurrentFilters={
                                    hasSelectedByCurrentFilters
                                }
                                onSelectedLineItemsChange={
                                    handleSelectedLineItemsChange
                                }
                                selectedCount={selectedInvoiceLineItemsCount}
                                hasSelection={lineItemsBulkActions.hasSelection}
                                onClearSelection={clearLineItemsSelection}
                                onSelectAllByFilters={selectAllByCurrentFilters}
                                onOpenBulkActions={
                                    lineItemsBulkActions.handleOpenBulkActions
                                }
                            />
                        </PaginatedInvoicesTableContextProvider>
                    )}
                    {viewOption.groupBy ===
                        InvoiceViewOptionGroup.COUNTERPARTY && (
                        <InvoicesByCounterpartyTableContextProvider>
                            <InvoicesByCustomerViewContainer
                                view={invoicesByCustomerTableView}
                                hasConnectedInvoiceAccounts={
                                    hasConnectedInvoiceAccounts
                                }
                                onClearFilters={handleClearFilters}
                            />
                        </InvoicesByCounterpartyTableContextProvider>
                    )}
                    {selectedInvoice && (
                        <DetailsAside>
                            <InvoiceDetails
                                invoice={
                                    selectedInvoice as EagerLoaded<
                                        Invoice,
                                        "lines"
                                    >
                                }
                                setInvoice={setSelectedInvoice}
                                onEdit={() =>
                                    setEditInvoices([selectedInvoice])
                                }
                                onUpdateMemo={handleUpdateInvoiceMemo}
                                onUpdateStatus={handleUpdateInvoiceStatus}
                                onUpdateDueDate={handleUpdateInvoiceDueDate}
                                scheduleCallbacks={
                                    invoiceDetailsScheduleCallbacks
                                }
                            />
                        </DetailsAside>
                    )}
                </div>
            </VStack>
            {scheduleOverlays}
            {!hasStripeAccounts && (
                <CreateStripeConnection
                    ref={stripeConnectionRef}
                    stripeLanding="login"
                    onCreated={noop}
                    toastMessage="Syncing Stripe Invoices... Check back in a few minutes."
                >
                    {() => null}
                </CreateStripeConnection>
            )}
            {!hasMercuryAccounts && (
                <CreateMercuryConnection
                    ref={mercuryConnectionRef}
                    onCreated={noop}
                >
                    {() => null}
                </CreateMercuryConnection>
            )}
            {!hasBillcomAccounts && (
                <CreateBillcomConnection
                    ref={billcomConnectionRef}
                    onCreated={noop}
                >
                    {() => null}
                </CreateBillcomConnection>
            )}
            <AccountSelectModal
                onHide={() => setShowAccountSelectModal(false)}
                show={showAccountSelectModal}
                invoicingAccounts={accountSelectAccounts}
                onSelect={onAccountSelect}
            />
            {isForwardOpen && (
                <ForwardViaEmailModal
                    show
                    mode="invoices"
                    onClose={() => setIsForwardOpen(false)}
                />
            )}
            {editInvoices && (
                <ManualInvoiceEditDrawer
                    invoices={editInvoices}
                    open
                    onClose={() => {
                        setEditInvoices(null);
                        setSelectedInvoice(undefined);
                        clearInvoiceSelection();
                        clearLineItemsSelection();
                    }}
                />
            )}
        </>
    );
};
